import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EntitySCV2Config } from '../configs/entitySCV2Config';
import FieldUpdateFlow from './FieldUpdateFlow';
import './MetadataEditor.css';

const FUF_FIELDS = new Set([
  'state_of_the_art', 'fto', 'patentability',
  'our_solution', 'industrialization', 'patent_application',
]);

/** Fields that open WorkspacePage for deep editing. */
const WORKSPACE_FIELDS = new Set([
  'state_of_the_art', 'features', 'fto_analysis',
  'patentability', 'patent_application', 'industrialization',
]);

interface FieldDefinition {
  fieldName: string;
  permission: 'editable' | 'display' | 'none';
  label: string;
  labelKey?: string;
  description: string;
}

interface MetadataEditorProps {
  entity: unknown;
  fieldConfig: EntitySCV2Config;
  activeField: string | null;
  expandedFields: Record<string, boolean>;
  highlightedFields: Record<string, boolean>;
  onFieldChange: (fieldName: string, value: string) => void;
  onFieldFocus: (fieldName: string) => void;
  onFieldBlur: () => void;
  onToggleExpand: (fieldName: string) => void;
  searchSessionsData?: unknown[];
  kbsData?: unknown[];
  selectedKbId?: string | null;
  onSelectKb?: (kbId: string) => void;
  onSelectSearch?: (sessionId: string) => void;
  pendingSearchTabs?: unknown[];
  onOpenContextKbTab?: (kbId: string, tabName: string) => void;
  onCreateContextKb?: (context: string, defaultName: string, tabName: string) => void;
  canCreateContextKb?: boolean;
  contextKbsData?: Record<string, { name: string; document_count: number }>;
  customFieldRenderers?: Record<string, React.ReactNode>;
  onOpenWorkspace?: (contextField: string) => void;
  entityType?: string;
  entityId?: string;
  onFufAccept?: (fieldName: string, value: string) => void;
}

const MetadataEditor: React.FC<MetadataEditorProps> = ({
  entity,
  fieldConfig,
  activeField,
  expandedFields,
  highlightedFields,
  onFieldChange,
  onFieldFocus,
  onFieldBlur,
  onToggleExpand,
  searchSessionsData = [],
  kbsData = [],
  selectedKbId = null,
  onSelectKb,
  onSelectSearch,
  pendingSearchTabs = [],
  onOpenContextKbTab,
  onCreateContextKb,
  canCreateContextKb = false,
  contextKbsData,
  customFieldRenderers,
  onOpenWorkspace,
  onFufAccept,
  entityType,
  entityId,
}) => {
  const [fufField, setFufField] = useState<string | null>(null);
  const { t } = useTranslation();

  // Map field name → context key / tab name / default KB name
  const FIELD_CONTEXT_MAP: Record<string, { context: string; tabName: string; defaultKbName: string }> = {
    state_of_the_art:   { context: 'state_of_the_art',  tabName: 'Docs_Solutions',        defaultKbName: 'State of the Art' },
  };
  // Format date for display
  const formatDate = (dateValue: unknown): string => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue as string | number | Date);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return String(dateValue);
    }
  };

  // Get field value as string
  const getFieldValue = (fieldName: string): string => {
    if (!entity) return '';
    const value = (entity as Record<string, unknown>)[fieldName];
    if (value === null || value === undefined) return '';
    
    // Handle date fields
    if (fieldName === 'created_date' || fieldName === 'last_updated' || fieldName.includes('date')) {
      return formatDate(value);
    }
    
    // Handle features_list object
    if (fieldName === 'features_list') {
      const featuresObj = value as { features?: Array<{ feature_name?: string }> };
      if (typeof value === 'object' && featuresObj.features && Array.isArray(featuresObj.features)) {
        if (featuresObj.features.length === 0) {
          return 'No features defined yet.';
        }
        return featuresObj.features.map((f: { feature_name?: string }, idx: number) => 
          `${idx + 1}. ${f.feature_name || 'Unnamed Feature'}`
        ).join('\n');
      }
      return 'No features available.';
    }

    // Handle search_sessions_list
    if (fieldName === 'search_sessions_list') {
      if (searchSessionsData.length === 0) {
        return 'No searches performed yet.';
      }
      return (searchSessionsData as Record<string, unknown>[]).map((session, idx: number) => {
        const date = new Date(session.created_date as string).toLocaleDateString();
        return `${idx + 1}. ${session.session_name || 'Unnamed Search'} (${date})`;
      }).join('\n');
    }

    // Handle state_of_the_art object (extract .content for display)
    if (fieldName === 'state_of_the_art') {
      if (typeof value === 'object' && value !== null) {
        return (value as { content?: string }).content ?? '';
      }
      return String(value);
    }

    // Handle kb_ids_list
    if (fieldName === 'kb_ids_list') {
      if (kbsData.length === 0) {
        return 'No knowledge bases linked.';
      }
      return (kbsData as Record<string, unknown>[]).map((kb, idx: number) => {
        const docCount = kb.document_count || 0;
        return `${idx + 1}. ${kb.name} (${docCount} documents)`;
      }).join('\n');
    }

    return String(value);
  };

  // Render special list fields with clickability
  const renderFieldContent = (fieldName: string, value: string) => {
    // Render search sessions as dropdown
    if (fieldName === 'search_sessions_list') {
      // Combine saved searches and pending tabs
      const allSearches = [
        ...(searchSessionsData as Record<string, unknown>[]).map((session) => ({
          id: session.id as string,
          type: 'saved',
          name: (session.session_name as string) || 'Unnamed Search',
          timestamp: new Date(session.created_date as string).toISOString(),
          searchType: (session.search_service as string) || 'unknown',
          kbName: '',
          docCount: (session.returned_documents as unknown[])?.length || 0,
        })),
        ...(pendingSearchTabs as Record<string, unknown>[]).map((tab) => ({
          id: tab.tabId as string,
          type: 'pending',
          name: tab.tabName as string,
          timestamp: tab.timestamp as string,
          searchType: (tab.searchType as string) || 'RAG',
          kbName: (tab.kbName as string) || '',
          docCount: (tab.documents as unknown[])?.length || 0,
        }))
      ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      if (allSearches.length === 0) {
        return <div className="metadata-field-readonly">No searches performed yet.</div>;
      }

      return (
        <div className="metadata-field-readonly searches-dropdown-row">
          <select
            className="searches-select"
            onChange={(e) => {
              if (e.target.value && onSelectSearch) {
                onSelectSearch(e.target.value);
                e.target.value = ''; // Reset dropdown
              }
            }}
            defaultValue=""
          >
            <option value="" disabled>
              {t('scv2.selectSearch', `Select a search (${allSearches.length} available)`)}
            </option>
            {allSearches.map((search) => {
              const date = new Date(search.timestamp).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
              const label = `${date} - ${search.searchType} - ${search.kbName || 'KB'} (${search.docCount} docs)${search.type === 'pending' ? ' *' : ''}`;
              return (
                <option key={search.id} value={search.id}>
                  {label}
                </option>
              );
            })}
          </select>
          <small className="searches-hint">* = currently open in tab</small>
        </div>
      );
    }

    // Render KBs as clickable list
    if (fieldName === 'kb_ids_list' && kbsData.length > 0) {
      const selected = (kbsData as Record<string, unknown>[]).find((kb) => kb.id === selectedKbId) || kbsData[0];
      return (
        <div className="metadata-field-readonly kb-select-row">
          <span className="kb-selected-label">{t('scv2.selectedKb', 'Selected KB:')}</span>
          <select
            className="kb-select"
            value={(selected as Record<string, unknown>)?.id as string || ''}
            onChange={(e) => onSelectKb && onSelectKb(e.target.value)}
          >
            {(kbsData as Record<string, unknown>[]).map((kb) => {
              return (
                <option key={kb.id as string} value={kb.id as string}>
                  {kb.name as string} ({kb.document_count as number ?? 0} documents)
                </option>
              );
            })}
          </select>
        </div>
      );
    }

    // Default rendering
    return <div className="metadata-field-readonly">{value || '\u2014'}</div>;
  };

  // Get visible fields from config
  const visibleFields: FieldDefinition[] = fieldConfig
    ? Object.entries(fieldConfig.fieldsPermissions)
        .filter(([_, permission]) => permission !== 'none')
        .map(([fieldName, permission]) => ({
          fieldName,
          permission: permission as 'editable' | 'display' | 'none',
          ...(fieldConfig.fieldLabels[fieldName] || { label: fieldName, description: '' }),
        }))
    : [];

  // Determine if field should be expandable based on content length
  const shouldBeExpandable = (fieldName: string, value: string): boolean => {
    // Always expandable if longer than 50 chars
    if (value.length > 50) return true;
    
    // Special fields that should always be expandable
    const alwaysExpandable = ['description', 'problem_statement', 'state_of_the_art', 'solutions', 'features_list'];
    return alwaysExpandable.includes(fieldName);
  };

  return (
    <>
    <div className="metadata-editor">
      {visibleFields.map(field => {
        if (customFieldRenderers?.[field.fieldName]) {
          return <React.Fragment key={field.fieldName}>{customFieldRenderers[field.fieldName]}</React.Fragment>;
        }
        const isExpanded = expandedFields[field.fieldName] || false;
        const isHighlighted = highlightedFields[field.fieldName] || false;
        const isActive = activeField === field.fieldName;
        const isEditable = field.permission === 'editable';
        const value = getFieldValue(field.fieldName);
        const isExpandable = shouldBeExpandable(field.fieldName, value);

        // Custom rendering for knowledge bases: single-line row without header/badge
        if (field.fieldName === 'kb_ids_list') {
          return (
            <div
              key={field.fieldName}
              className={`metadata-field-row expanded ${isHighlighted ? 'highlighted' : ''} ${isActive ? 'active' : ''}`}
            >
              <div className="metadata-field-body kb-inline">
                {renderFieldContent(field.fieldName, value)}
              </div>
            </div>
          );
        }

        // Custom rendering for search sessions: single-line row with label and dropdown
        if (field.fieldName === 'search_sessions_list') {
          return (
            <div
              key={field.fieldName}
              className={`metadata-field-row expanded ${isHighlighted ? 'highlighted' : ''} ${isActive ? 'active' : ''}`}
            >
              <div className="metadata-field-body searches-inline">
                <span className="searches-label">{field.labelKey ? t(field.labelKey, field.label) : field.label}:</span>
                {renderFieldContent(field.fieldName, value)}
              </div>
            </div>
          );
        }

        return (
          <div
            key={field.fieldName}
            className={`metadata-field-row ${isExpanded ? 'expanded' : 'collapsed'} ${isHighlighted ? 'highlighted' : ''} ${isActive ? 'active' : ''}`}
          >
            <div
              className="metadata-field-header"
              onClick={() => isExpandable && onToggleExpand(field.fieldName)}
              style={{ cursor: isExpandable ? 'pointer' : 'default' }}
            >
              {isExpandable && (
                <span className="expand-toggle">{isExpanded ? '▼' : '▶'}</span>
              )}
              {!isExpandable && (
                <span className="expand-toggle-spacer"></span>
              )}
              {onOpenWorkspace && WORKSPACE_FIELDS.has(field.fieldName) ? (
                <span
                  className="field-label field-label-link"
                  title={t('workspace.openButton.title', 'Open in workspace')}
                  onClick={(e) => { e.stopPropagation(); onOpenWorkspace(field.fieldName); }}
                >
                  {field.labelKey ? t(field.labelKey, field.label) : field.label}
                </span>
              ) : (
                <span className="field-label">{field.labelKey ? t(field.labelKey, field.label) : field.label}</span>
              )}
              {FIELD_CONTEXT_MAP[field.fieldName] && (() => {
                const { context, tabName, defaultKbName } = FIELD_CONTEXT_MAP[field.fieldName];
                const kbId = (entity as { context_kbs?: Record<string, string> })?.context_kbs?.[context];
                const kbInfo = kbId ? contextKbsData?.[kbId] : null;
                if (kbId) {
                  const shortName = kbInfo
                    ? (kbInfo.name.length > 15 ? kbInfo.name.substring(0, 14) + '\u2026' : kbInfo.name)
                    : 'KB';
                  return (
                    <button
                      className="context-kb-badge"
                      onClick={(e) => { e.stopPropagation(); onOpenContextKbTab?.(kbId, tabName); }}
                      title={kbInfo ? `${kbInfo.name} (${kbInfo.document_count} docs)` : `Open ${defaultKbName} KB`}
                    >
                      <span className="cfkb-name">{shortName}</span>
                      {kbInfo && <span className="cfkb-count">{kbInfo.document_count} docs</span>}
                    </button>
                  );
                } else if (canCreateContextKb) {
                  return (
                    <button
                      className="context-create-btn"
                      onClick={(e) => { e.stopPropagation(); onCreateContextKb?.(context, defaultKbName, tabName); }}
                      title={`Create ${defaultKbName} KB`}
                    >
                      + KB
                    </button>
                  );
                }
                return null;
              })()}
              {entityType === 'sub-project' && entityId && FUF_FIELDS.has(field.fieldName) && (
                <button
                  title={t('fieldUpdate.buttonTitle', 'Update this field from a Knowledge Base')}
                  onClick={(e) => { e.stopPropagation(); setFufField(field.fieldName); }}
                  style={{
                    fontSize: 10, padding: '1px 7px', borderRadius: 3,
                    border: '1px solid #059669',
                    background: '#ecfdf5', color: '#059669',
                    fontWeight: 600, cursor: 'pointer',
                    flexShrink: 0, whiteSpace: 'nowrap',
                  }}
                >
                  {t('fieldUpdate.buttonLabel', '↑ KB')}
                </button>
              )}
              {!isEditable && <span className="readonly-badge">Read-only</span>}
              {!isExpanded && (
                <span className="field-preview">
                  {value.substring(0, 80)}{value.length > 80 ? '...' : ''}
                </span>
              )}
            </div>

            {isExpanded && (
              <div className="metadata-field-body">
                {isEditable ? (
                  <textarea
                    value={value}
                    onChange={(e) => onFieldChange(field.fieldName, e.target.value)}
                    onFocus={() => onFieldFocus(field.fieldName)}
                    onBlur={onFieldBlur}
                    className="metadata-field-textarea"
                    placeholder={field.description}
                    rows={Math.min(Math.max(3, Math.ceil(value.length / 80)), 20)}
                  />
                ) : (
                  renderFieldContent(field.fieldName, value)
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>

    {fufField && entityId && (
      <FieldUpdateFlow
        subProjectId={entityId}
        fieldName={fufField}
        fieldValue={(() => {
          const raw = (entity as Record<string, unknown>)?.[fufField] ?? '';
          if (typeof raw === 'object' && raw !== null && 'content' in raw) return (raw as { content?: string }).content ?? '';
          return typeof raw === 'string' ? raw : '';
        })()}
        onAccept={(newText) => (onFufAccept ?? onFieldChange)(fufField, newText)}
        onClose={() => setFufField(null)}
      />
    )}
    </>
  );
};

export default MetadataEditor;
